import { useState } from "react"
import { ChevronDown, ChevronUp } from 'lucide-react'
import { Button } from "./ui/Button"
import { useTheme } from "./ThemeContext"

export default function EducationExperience() {
  const [isExpanded, setIsExpanded] = useState(true)
  const { theme } = useTheme()

  return (
    <section
      id="experience"
      className={`py-16 relative transition-colors duration-300 ${
        theme === "dark" ? "bg-gradient-to-r from-gray-900 to-blue-900" : "bg-gray-50"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4">
        <div className="text-center mb-12">
          <h2
            className={`text-4xl font-bold mb-4 transition-colors duration-300 ${
              theme === "dark" ? "text-white" : "text-gray-800"
            }`}
          >
            Education & Experiences
          </h2>
          <Button
            variant="ghost"
            onClick={() => setIsExpanded(!isExpanded)}
            className="text-blue-500 hover:text-blue-700"
          >
            Collapse {isExpanded ? <ChevronUp className="ml-2 h-4 w-4" /> : <ChevronDown className="ml-2 h-4 w-4" />}
          </Button>
        </div>

        {isExpanded && (
          <div className="space-y-16">
            {/* Work Experience */}
            <div>
              <h3
                className={`text-3xl font-bold mb-8 transition-colors duration-300 ${
                  theme === "dark" ? "text-white" : "text-gray-800"
                }`}
              >
                Work Experience
              </h3>

              {/* Data Nova Experience */}
              <div
                className={`mb-8 p-6 rounded-lg shadow-md hover:border-2 hover:border-yellow-400 transition-all duration-300 cursor-pointer ${
                  theme === "dark"
                    ? "bg-gray-800 hover:bg-gray-700 border border-gray-700"
                    : "bg-white hover:border-blue-400"
                }`}
              >
                <div className="flex items-start space-x-6">
                  <img
                    src="/images/datanova-logo.png"
                    alt="Data Nova Logo"
                    width={120}
                    height={60}
                    className="object-contain"
                  />
                  <div className="flex-1">
                    <div className="text-yellow-400 text-lg mb-2">March 2024 - Present</div>
                    <h4
                      className={`text-2xl font-bold mb-2 transition-colors duration-300 ${
                        theme === "dark" ? "text-white" : "text-gray-800"
                      }`}
                    >
                      Junior Software Engineer
                    </h4>
                    <p
                      className={`mb-4 transition-colors duration-300 ${
                        theme === "dark" ? "text-gray-300" : "text-gray-600"
                      }`}
                    >
                      Web Development • Noida, U.P.
                    </p>
                    <ul
                      className={`space-y-2 transition-colors duration-300 ${
                        theme === "dark" ? "text-gray-200" : "text-gray-700"
                      }`}
                    >
                      <li>
                        • Engaged in FullStack Development, excelling in debugging, API integration, and database
                        management for optimal performance.
                      </li>
                      <li>
                        • Adapted to evolving requirements, ensuring the seamless delivery of robust applications in a
                        fast-paced environment.
                      </li>
                      <li>
                        • Partnered with testing, support, and database groups to address user needs and resolve
                        realtime issues.
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* KSPL Experience */}
              <div
                className={`p-6 rounded-lg shadow-md hover:border-2 hover:border-yellow-400 transition-all duration-300 cursor-pointer ${
                  theme === "dark"
                    ? "bg-gray-800 hover:bg-gray-700 border border-gray-700"
                    : "bg-white hover:border-blue-400"
                }`}
              >
                <div className="flex items-start space-x-6">
                  <img
                    src="/images/kspl-logo.png"
                    alt="KSPL Logo"
                    width={80}
                    height={80}
                    className="object-contain"
                  />
                  <div className="flex-1">
                    <div className="text-yellow-400 text-lg mb-2">October 2023 - February 2024</div>
                    <h4
                      className={`text-2xl font-bold mb-2 transition-colors duration-300 ${
                        theme === "dark" ? "text-white" : "text-gray-800"
                      }`}
                    >
                      Web Developer Intern
                    </h4>
                    <p
                      className={`mb-4 transition-colors duration-300 ${
                        theme === "dark" ? "text-gray-300" : "text-gray-600"
                      }`}
                    >
                      Maneshar, Gurgaon
                    </p>
                    <ul
                      className={`space-y-2 transition-colors duration-300 ${
                        theme === "dark" ? "text-gray-200" : "text-gray-700"
                      }`}
                    >
                      <li>
                        • Engaged with user teams to comprehensively grasp requirements, leading the design and
                        development of front-end and back-end systems
                      </li>
                      <li>
                        • Leveraged MySQL, PHP, jQuery, and AJAX to architect and implement robust databases and dynamic
                        interfaces, ensuring seamless user interaction and functionality
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Education */}
            <div>
              <h3
                className={`text-3xl font-bold mb-8 transition-colors duration-300 ${
                  theme === "dark" ? "text-white" : "text-gray-800"
                }`}
              >
                Education
              </h3>

              {/* RKGIT - B.Tech */}
              <div
                className={`mb-8 p-6 rounded-lg shadow-md hover:border-2 hover:border-yellow-400 transition-all duration-300 cursor-pointer ${
                  theme === "dark"
                    ? "bg-gray-800 hover:bg-gray-700 border border-gray-700"
                    : "bg-white hover:border-blue-400"
                }`}
              >
                <div className="flex items-start space-x-6">
                  <img
                    src="/images/aktu-logo.jpg"
                    alt="AKTU Logo"
                    width={100}
                    height={100}
                    className="object-contain"
                  />
                  <div className="flex-1">
                    <div className="text-yellow-400 text-lg mb-2">Class of 2024</div>
                    <h4
                      className={`text-2xl font-bold mb-2 transition-colors duration-300 ${
                        theme === "dark" ? "text-white" : "text-gray-800"
                      }`}
                    >
                      RKGIT • AKTU Lucknow • 7.8 CGPA
                    </h4>
                    <p
                      className={`mb-4 transition-colors duration-300 ${
                        theme === "dark" ? "text-gray-300" : "text-gray-600"
                      }`}
                    >
                      B.Tech. in Computer Science & Engineering
                    </p>
                    <div>
                      <p
                        className={`font-semibold mb-2 transition-colors duration-300 ${
                          theme === "dark" ? "text-gray-200" : "text-gray-700"
                        }`}
                      >
                        Relevant Coursework:
                      </p>
                      <ul
                        className={`space-y-1 transition-colors duration-300 ${
                          theme === "dark" ? "text-gray-200" : "text-gray-700"
                        }`}
                      >
                        <li>• Database Management System</li>
                        <li>• Data Structures and Algorithms</li>
                        <li>• Computer Network</li>
                        <li>• Object Oriented Programming</li>
                        <li>• Web Development</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              {/* CBSE - Intermediate (12th) */}
              <div
                className={`mb-8 p-6 rounded-lg shadow-md hover:border-2 hover:border-yellow-400 transition-all duration-300 cursor-pointer ${
                  theme === "dark"
                    ? "bg-gray-800 hover:bg-gray-700 border border-gray-700"
                    : "bg-white hover:border-blue-400"
                }`}
              >
                <div className="flex items-start space-x-6">
                  <img
                    src="/images/cbse-logo.jpg"
                    alt="CBSE Logo"
                    width={100}
                    height={100}
                    className="object-contain"
                  />
                  <div className="flex-1">
                    <div className="text-yellow-400 text-lg mb-2">Class of 2020</div>
                    <h4
                      className={`text-2xl font-bold mb-2 transition-colors duration-300 ${
                        theme === "dark" ? "text-white" : "text-gray-800"
                      }`}
                    >
                      Intermediate (12th) • 86.7%
                    </h4>
                    <p
                      className={`transition-colors duration-300 ${
                        theme === "dark" ? "text-gray-300" : "text-gray-600"
                      }`}
                    >
                      PCM (English & Hindi)
                    </p>
                  </div>
                </div>
              </div>

              {/* CBSE - High School (10th) */}
              <div
                className={`p-6 rounded-lg shadow-md hover:border-2 hover:border-yellow-400 transition-all duration-300 cursor-pointer ${
                  theme === "dark"
                    ? "bg-gray-800 hover:bg-gray-700 border border-gray-700"
                    : "bg-white hover:border-blue-400"
                }`}
              >
                <div className="flex items-start space-x-6">
                  <img
                    src="/images/cbse-logo.jpg"
                    alt="CBSE Logo"
                    width={100}
                    height={100}
                    className="object-contain"
                  />
                  <div className="flex-1">
                    <div className="text-yellow-400 text-lg mb-2">Class of 2018</div>
                    <h4
                      className={`text-2xl font-bold mb-2 transition-colors duration-300 ${
                        theme === "dark" ? "text-white" : "text-gray-800"
                      }`}
                    >
                      Highschool (10th) • 91%
                    </h4>
                    <p
                      className={`transition-colors duration-300 ${
                        theme === "dark" ? "text-gray-300" : "text-gray-600"
                      }`}
                    >
                      Maths & Science
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="absolute top-0 right-0 w-1/4 h-full bg-gradient-to-l from-blue-500 to-purple-600 opacity-20 rounded-bl-full"></div>
    </section>
  )
}